import React from "react"
import PropTypes from "prop-types"

/**
 * StatusBadge Component
 * A reusable status badge component with consistent styling
 * 
 * @param {string} status - Status value (pending, approved, rejected, active, inactive, etc.)
 * @param {string} customText - Optional custom text to display (defaults to status)
 * @param {string} className - Additional CSS classes
 */
const StatusBadge = ({ status, customText, className = "" }) => {
  const normalizedStatus = status?.toLowerCase() || "pending"
  const displayText = customText || formatStatusText(status)
  
  return (
    <span className={`status-badge ${normalizedStatus} ${className}`}>
      {displayText}
    </span>
  )
}

/**
 * Format status text for display
 * @param {string} status - Raw status value
 * @returns {string} Formatted status text
 */
const formatStatusText = (status) => {
  if (!status) return "N/A"
  
  const statusMap = {
    approve: "Approved",
    approved: "Approved",
    reject: "Rejected",
    rejected: "Rejected",
    pending: "Pending",
    active: "Active",
    inactive: "Inactive",
    verified: "Verified",
    success: "Success",
    danger: "Failed",
    // Business-specific statuses
    VERIFIED: "Verified",
    REJECTED: "Rejected", 
    DOCUMENT_SUBMITTED: "Document Submitted",
    ACCOUNT_CREATED: "Account Created",
  }
  
  return statusMap[status.toLowerCase()] || capitalize(status)
}

/**
 * Capitalize first letter of each word
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
const capitalize = (str) => {
  return str
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
  customText: PropTypes.string,
  className: PropTypes.string,
}

export default StatusBadge
